// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import classNames from 'classnames';
import { useContext, useMemo, useRef } from 'react';
import { CLS_PREFIX } from '../../constants';
import Button from '../Button';
import InPlaceEdit from '../InPlaceEdit';
import { isClickable } from '../utils';
import VerticalContext from '../VerticalContext';
import SectionContext from './SectionContext';
import { useCollapsed } from './useCollapsed';
import { useVertical } from './useVertical';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const SECTION_BASE_CLASS_NAME = `${CLS_PREFIX}section`;

/**
 * `Section` component is used for dividing of complex content into groups for better readability
 * and comprehension of the presented information.
 * @since 0.0.54
 */
const Section = _ref => {
  let {
    baseClassName = SECTION_BASE_CLASS_NAME,
    className,
    intent,
    collapsible = false,
    collapsed: externalCollapsed = false,
    onToggle,
    title,
    onTitleChange,
    onClose,
    buttons,
    vertical: externalVertical,
    children,
    ...props
  } = _ref;
  const containerRef = useRef(null);
  const collapseButtonRef = useRef(null);
  const vertical = useVertical(externalVertical, containerRef);
  const [collapsed, setCollapsed] = useCollapsed(externalCollapsed);
  const toggleCollapsed = () => {
    setCollapsed(prevCollapsed => {
      onToggle?.(!prevCollapsed);
      return !prevCollapsed;
    });
  };
  const parentSectionContext = useContext(SectionContext);
  const sectionContextValue = useMemo(() => ({
    expand: () => {
      parentSectionContext?.expand();
      setCollapsed(false);
    }
  }), [setCollapsed, parentSectionContext]);
  return /*#__PURE__*/_jsx(SectionContext.Provider, {
    value: sectionContextValue,
    children: /*#__PURE__*/_jsx(VerticalContext.Provider, {
      value: vertical,
      children: /*#__PURE__*/_jsxs("div", {
        ref: containerRef,
        className: classNames(baseClassName, {
          [`${baseClassName}--${intent}`]: intent,
          [`${baseClassName}--collapsible`]: collapsible,
          [`${baseClassName}--collapsed`]: collapsed,
          [`${baseClassName}--vertical`]: vertical
        }, className),
        ...props,
        children: [title && /*#__PURE__*/_jsxs("div", {
          className: `${baseClassName}__header`,
          children: [collapsible && /*#__PURE__*/_jsx(Button, {
            ref: collapseButtonRef,
            ghost: true,
            className: classNames(`${baseClassName}__control-button`, `${baseClassName}__control-button--collapse`),
            icon: {
              name: 'chevron-up',
              flipVertical: collapsed
            },
            onClick: toggleCollapsed,
            "aria-expanded": !collapsed
          }), /*#__PURE__*/_jsx(InPlaceEdit, {
            className: `${baseClassName}__title`,
            value: title,
            onChange: onTitleChange,
            onClick: e => {
              if (!collapsible || isClickable(e)) {
                return;
              }
              collapseButtonRef.current?.click();
            }
          }), (onClose || buttons) && /*#__PURE__*/_jsxs("div", {
            className: `${baseClassName}__header-buttons`,
            children: [buttons, onClose && /*#__PURE__*/_jsx(Button, {
              ghost: true,
              className: classNames(`${baseClassName}__control-button`, `${baseClassName}__control-button--close`),
              onClick: onClose,
              icon: "cross-mark"
            })]
          })]
        }), /*#__PURE__*/_jsx("div", {
          style: {
            display: collapsed ? 'none' : undefined
          },
          children: children
        })]
      })
    })
  });
};
export default Section;